import React, { useMemo, useCallback, useContext } from 'react';
import styled, { css } from 'styled-components';
import { FlexMiddle, Tooltip, ApproveIcon } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import privileges from '@ubeya/shared-web/privileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { INVOICE_STATUS, PAYROLL_PROCESS_STATUS } from '@ubeya/shared-web/constants';
import ExternalApproveRenderer from './ExternalApproveRenderer';

const Wrapper = styled(FlexMiddle)`
  justify-content: flex-end;
  padding: 0 2px;
  gap: 4px;

  .read-only {
    cursor: not-allowed;
  }

  .hidden {
    visibility: hidden;
  }
`;

const ApproveButton = styled(ApproveIcon)`
  min-width: 26px;

  ${({ $isUneditable }) => $isUneditable && 'cursor:not-allowed'};

  ${({ $hasChangedAfterApprove }) =>
    $hasChangedAfterApprove &&
    css`
      circle {
        stroke: ${({ theme }) => theme.colors.disabled};
        fill: ${({ theme }) => theme.colors.disabled};
      }
    `}
`;

const isFilledJob = ({ dummy, actualIn, actualOut, jobTypeId }) => jobTypeId !== 1 || (!dummy && actualIn && actualOut);

const ApproveRenderer = ({
  rowData,
  columnData,
  rowData: {
    approved,
    hasChangedAfterApprove,
    summarized,
    isExternal,
    actualIn,
    isJob,
    isPto,
    isLeave,
    actualOut,
    actualCheckIn,
    actualCheckOut,
    clientApproved,
    clientDeclined,
    id: slotId,
    timesheetId,
    originSupplierData,
    supplierData: { branchId: supplierBranchId } = {}
  },
  columnData: { approveTimeSheet, readOnly: generalReadOnly, editOSTimeSheet }
}) => {
  const myPrivileges = useContext(PrivilegesContext);
  const { t, payrollCheckInOut: requireCheckInCheckOut } = useContext(ConfigContext);

  const hasPrivilegeToApproveTimesheets = !!myPrivileges[privileges.APPROVE_TIMESHEET];
  const isRowProcessed = rowData?.payrollProcess?.status === PAYROLL_PROCESS_STATUS.PROCESSED;

  // hack: we assume isjob timesheets are always pending
  // we need to check if this is part of event with client not if its job, for now...
  // Leaves never have a client invoice, so they're treated as pending here.
  const isSlotInvoicePending = isJob || isPto || isLeave || rowData?.invoiceStatus === INVOICE_STATUS.PENDING;

  const canApproveRow = hasPrivilegeToApproveTimesheets && !isRowProcessed && !generalReadOnly && isSlotInvoicePending;

  const tooltip = useMemo(() => {
    if (!hasPrivilegeToApproveTimesheets)
      return t('missingPrivilege', {
        privilege: t('adminPrivilegesApproveTimesheet')
      });
    if (isRowProcessed) return t('timesheetIsProcessedTooltip');
    if (hasChangedAfterApprove) return t('hasChangedAfterApprove');
    if (!isSlotInvoicePending) return t('slotHasAlreadyIssuedInInvoice');
    if (approved) return t('unApprove');
    return t('approve');
  }, [hasPrivilegeToApproveTimesheets, t, isRowProcessed, hasChangedAfterApprove, isSlotInvoicePending, approved]);

  const onApproval = useCallback(() => {
    if (!canApproveRow) return;

    return approveTimeSheet({ ...rowData, approved: !approved });
  }, [canApproveRow, approveTimeSheet, rowData, approved]);

  const shouldDisplayApproveButton = useMemo(() => {
    // Leaves / PTOs aren't gated on check-in/out like shift timesheets.
    if (approved || isPto || isLeave) {
      return true;
    }

    if (requireCheckInCheckOut) {
      return !!actualCheckIn && !!actualCheckOut;
    }

    return isFilledJob(rowData);
  }, [actualCheckIn, actualCheckOut, approved, isPto, isLeave, requireCheckInCheckOut, rowData]);

  if (summarized) {
    return <></>;
  }

  if (isExternal && !!editOSTimeSheet) {
    return (
      <ExternalApproveRenderer
        rowData={{
          actualIn,
          actualOut,
          clientApproved,
          clientDeclined,
          slotId,
          timesheetId,
          supplierBranchId,
          originSupplierData
        }}
        columnData={columnData}
      />
    );
  }

  return (
    shouldDisplayApproveButton && (
      <Wrapper>
        <Tooltip body={tooltip}>
          <ApproveButton
            data-testid="timesheet-approve-button"
            data-approved={String(approved)}
            className={!approved ? (!canApproveRow ? 'read-only' : 'editable') : undefined}
            $isActive={approved}
            $hasChangedAfterApprove={hasChangedAfterApprove}
            $isUneditable={isRowProcessed || generalReadOnly}
            onClick={onApproval}
          />
        </Tooltip>
        {/* This hidden button ensures alignment with cells that display two icons (e.g., approve/decline) */}
        <ApproveButton className="hidden" />
      </Wrapper>
    )
  );
};

export default ApproveRenderer;
